import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Search, Gavel, ExternalLink, MessageSquareText, Loader2 } from "lucide-react";

export default function DecisionsDialog({ open, onOpenChange, onAsk, jurisdictionScope = "federal", stateCode = "" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    setLoading(true);
    setError("");
    try {
      const params = { q, jurisdiction_scope: jurisdictionScope };
      if (jurisdictionScope === "state_local" && stateCode) params.state = stateCode;
      const r = await api.get(`/decisions`, { params });
      setResults(r.data || []);
      setSearched(true);
    } catch (e) {
      // A failed search is not "no matches": say what happened and drop old results.
      setResults([]);
      setSearched(false);
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Case law search failed. Check your connection and try again.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none bg-card border-border" data-testid="decisions-dialog">
        <DialogHeader>
          <span className="overline text-primary">Case Law Search</span>
          <DialogTitle className="font-serif-display text-2xl">Find citable decisions</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          Search real, citable authority — e.g. <span className="text-foreground">"wrongful termination truck driver"</span> surfaces the STAA contributing-factor line and winning-standard decisions.
        </p>
        <div className="flex gap-2 mt-1">
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="decisions-search-input"
                 onKeyDown={(e) => e.key === "Enter" && run()}
                 placeholder="Search cases, standards, or your cause…" className="rounded-none bg-secondary border-border" />
          <Button onClick={run} disabled={loading} data-testid="decisions-search-btn"
                  className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 shrink-0">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
        <div className="space-y-2.5 max-h-[52vh] overflow-y-auto pr-1 mt-2">
          {error && <p className="text-sm text-destructive py-6 text-center" role="alert" data-testid="decisions-search-error">{error}</p>}
          {searched && results.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No matching decisions. Try broader terms.</p>}
          {results.map((d, i) => (
            <div key={i} className="border border-border/60 bg-background/40 p-4" data-testid={`decision-${i}`}>
              <div className="flex items-start gap-2">
                <Gavel className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <h4 className="font-serif-display text-base font-semibold leading-tight">{d.case_name}</h4>
                  <div className="font-mono-tech text-xs text-primary mt-0.5">{d.citation}</div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px] text-muted-foreground">
                    <span>{d.forum}</span>
                    <span className="text-foreground/80">· {d.outcome}</span>
                  </div>
                  <p className="text-sm text-foreground/85 mt-2 leading-relaxed">{d.summary}</p>
                  <div className="flex items-center gap-3 mt-3">
                    <button onClick={() => { onAsk(`Explain how "${d.case_name}" (${d.citation}) applies to my situation, and how I could use it to prepare my filing.`); onOpenChange(false); }}
                            data-testid={`decision-ask-${i}`}
                            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                      <MessageSquareText className="w-3.5 h-3.5" /> Ask Leego Beego about this case
                    </button>
                    {d.source_url && (
                      <a href={d.source_url} target="_blank" rel="noreferrer" data-testid={`decision-source-${i}`}
                         className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary">
                        <ExternalLink className="w-3.5 h-3.5" /> Official source
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
